import {
  ESG_CONSTANT_GRID_INTENSITY_CAVEAT,
  ESG_FRAMING_LINE,
  ESG_LOCATION_BASED_METHOD_BADGE,
  ESG_LOCATION_BASED_METHOD_NOTE,
} from '@siliconx/shared';

/**
 * The three standard disclosures every SIL-442 (W4) carbon surface carries:
 * the §1 framing line, the §2 location-based method badge + note, and the
 * §3 constant-grid-intensity chart footnote.
 *
 * EVERY string here comes from `@siliconx/shared`'s locked copy constants
 * (`dto/esg-export.ts`, the SIL-426 legally-vetted inventory) — never
 * retyped, never paraphrased, never truncated to fit a layout. If a string
 * does not fit, change the layout. The same constants back the CSV export
 * headers, so a screenshot of this page and the exported file can never
 * disagree.
 */

/** §1 — global framing. Rendered as a standalone, screenshot-safe line
 * (not a tooltip): a cropped screenshot of this page must still carry it. */
export function CarbonFramingLine({ className = '' }: { className?: string }) {
  return (
    <p className={`text-sm font-medium text-violet-900 ${className}`} data-testid="carbon-framing-line">
      {ESG_FRAMING_LINE}
    </p>
  );
}

/** §2 — the method chip. Pairs with `LocationBasedMethodNote` below; the
 * badge alone is never sufficient disclosure. */
export function LocationBasedMethodBadge() {
  return (
    <span
      className="inline-flex items-center rounded border border-violet-300 bg-white px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wider text-violet-700"
      data-testid="location-based-method-badge"
    >
      {ESG_LOCATION_BASED_METHOD_BADGE}
    </span>
  );
}

/** §2 — the market-based note that must accompany the badge. */
export function LocationBasedMethodNote({ className = '' }: { className?: string }) {
  return (
    <p className={`text-xs leading-relaxed text-violet-800 ${className}`} data-testid="location-based-method-note">
      {ESG_LOCATION_BASED_METHOD_NOTE}
    </p>
  );
}

/** §3 — constant-grid-intensity caveat, rendered as the chart's footnote
 * (SIL-423: this belongs immediately under the trajectory, not in a
 * collapsed help panel). */
export function ConstantGridIntensityFootnote({ className = '' }: { className?: string }) {
  return (
    <p
      className={`text-xs leading-relaxed text-amber-800 ${className}`}
      data-testid="constant-grid-intensity-footnote"
    >
      {ESG_CONSTANT_GRID_INTENSITY_CAVEAT}
    </p>
  );
}
